import React, { useState, useEffect, useCallback, useMemo } from 'react';
import AdminLayout from '../../components/AdminLayout';
import adminApi from '../../services/adminApi';

// S0 Curriculum Gap Engine — admin surface.
// Three views (tabs): Syllabi (upload + unit editor + JD paste), Runs
// (list + stale flag + status), Gap Map (per-run verdicts + unresolved
// review). One file, no sub-component sprawl — the state graph is small
// enough that co-location aids the review the user has been doing.

const TABS = [
  { id: 'syllabi', label: 'Syllabi' },
  { id: 'runs',    label: 'Runs' },
  { id: 'gapmap',  label: 'Gap Map' },
];

function VerdictBadge({ verdict }) {
  const color = {
    covered: '#059669',
    gap:     '#dc2626',
    surplus: '#d97706',
    absent:  '#6b7280',
  }[verdict] || '#6b7280';
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 4,
      background: color, color: 'white', fontSize: 12, fontWeight: 600,
    }}>{verdict}</span>
  );
}

function StatusBadge({ status, stale }) {
  const color =
      stale                 ? '#d97706'
    : status === 'complete' ? '#059669'
    : status === 'failed'   ? '#dc2626'
    : status === 'running'  ? '#2563eb'
    :                         '#6b7280';
  const label = stale ? `${status} (stale)` : status;
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 4,
      background: color, color: 'white', fontSize: 12, fontWeight: 600,
    }}>{label}</span>
  );
}

export default function CurriculumGaps() {
  const [tab, setTab] = useState('syllabi');

  // ── shared: domains + syllabi list ──
  const [domains, setDomains] = useState([]);
  const [syllabi, setSyllabi] = useState([]);
  const [selectedSyllabusId, setSelectedSyllabusId] = useState(null);

  // ── syllabi tab ──
  const [uploadForm, setUploadForm] = useState({
    institutionName: '', programmeName: '', domainId: '', file: null, extractedText: '',
  });
  const [uploading, setUploading] = useState(false);
  const [syllabusDetail, setSyllabusDetail] = useState(null);
  const [jds, setJds] = useState([]);
  const [newJdText, setNewJdText] = useState('');
  const [newJdLabel, setNewJdLabel] = useState('');
  const [selectedJdIds, setSelectedJdIds] = useState([]);
  const [triggering, setTriggering] = useState(false);

  // ── runs tab ──
  const [runs, setRuns] = useState([]);
  const [selectedRunId, setSelectedRunId] = useState(null);
  const [runDetail, setRunDetail] = useState(null);

  // ── gap map tab ──
  const [gapMap, setGapMap] = useState([]);
  const [candidates, setCandidates] = useState([]);

  // ── global state ──
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);

  const clearMessages = () => { setError(null); setInfo(null); };

  // ── initial load ──
  const loadDomains = useCallback(async () => {
    try {
      const data = await adminApi.listGapDomains();
      setDomains(data.domains || []);
    } catch (err) {
      setError(`Load domains: ${err.response?.data?.error || err.message}`);
    }
  }, []);

  const loadSyllabi = useCallback(async () => {
    try {
      const data = await adminApi.listSyllabi();
      setSyllabi(data.syllabi || []);
    } catch (err) {
      setError(`Load syllabi: ${err.response?.data?.error || err.message}`);
    }
  }, []);

  useEffect(() => {
    loadDomains();
    loadSyllabi();
  }, [loadDomains, loadSyllabi]);

  // ── syllabus detail (with JDs) ──
  const loadSyllabusDetail = useCallback(async (id) => {
    if (!id) { setSyllabusDetail(null); setJds([]); return; }
    try {
      const [detail, jdData] = await Promise.all([
        adminApi.getSyllabus(id),
        adminApi.listSyllabusJds(id),
      ]);
      setSyllabusDetail(detail.syllabus);
      setJds(jdData.jds || []);
      setSelectedJdIds([]);
    } catch (err) {
      setError(`Load syllabus: ${err.response?.data?.error || err.message}`);
    }
  }, []);

  useEffect(() => {
    loadSyllabusDetail(selectedSyllabusId);
  }, [selectedSyllabusId, loadSyllabusDetail]);

  // ── upload handler ──
  const onUpload = async (e) => {
    e.preventDefault();
    clearMessages();
    if (!uploadForm.institutionName || !uploadForm.programmeName || !uploadForm.domainId) {
      setError('Institution, programme, and domain are required'); return;
    }
    if (!uploadForm.file && !uploadForm.extractedText.trim()) {
      setError('Provide a file or paste extracted text'); return;
    }
    setUploading(true);
    try {
      const data = await adminApi.uploadSyllabus(uploadForm);
      setInfo(`Uploaded — ${data.proposedUnits?.length || 0} units proposed`
              + ` (strategy: ${data.proposedUnits?.[0]?.splitStrategy || 'n/a'})`);
      setUploadForm({ institutionName: '', programmeName: '', domainId: '', file: null, extractedText: '' });
      await loadSyllabi();
      setSelectedSyllabusId(data.syllabus.id);
    } catch (err) {
      setError(`Upload: ${err.response?.data?.error || err.message}`);
    } finally {
      setUploading(false);
    }
  };

  // ── unit editor ──
  const updateUnitText = (idx, newText) => {
    const next = [...(syllabusDetail.units || [])];
    next[idx] = { ...next[idx], text: newText };
    setSyllabusDetail({ ...syllabusDetail, units: next });
  };
  const updateUnitHeading = (idx, newHeading) => {
    const next = [...(syllabusDetail.units || [])];
    next[idx] = { ...next[idx], heading: newHeading };
    setSyllabusDetail({ ...syllabusDetail, units: next });
  };
  const removeUnit = (idx) => {
    const next = (syllabusDetail.units || []).filter((_, i) => i !== idx)
      .map((u, i) => ({ ...u, ordinal: i + 1 }));
    setSyllabusDetail({ ...syllabusDetail, units: next });
  };
  const addUnit = () => {
    const next = [...(syllabusDetail.units || []),
      { ordinal: (syllabusDetail.units || []).length + 1,
        heading: 'New Unit', text: '', splitStrategy: 'manual' }];
    setSyllabusDetail({ ...syllabusDetail, units: next });
  };
  const saveUnits = async () => {
    clearMessages();
    try {
      await adminApi.updateSyllabusUnits(syllabusDetail.id, syllabusDetail.units || []);
      setInfo('Units saved');
      await loadSyllabi();
    } catch (err) {
      setError(`Save units: ${err.response?.data?.error || err.message}`);
    }
  };

  // ── JD paste ──
  const addJd = async () => {
    clearMessages();
    if (!newJdText.trim()) { setError('JD text required'); return; }
    try {
      await adminApi.addSyllabusJd(selectedSyllabusId, { text: newJdText, label: newJdLabel });
      setNewJdText(''); setNewJdLabel('');
      await loadSyllabusDetail(selectedSyllabusId);
    } catch (err) {
      setError(`Add JD: ${err.response?.data?.error || err.message}`);
    }
  };

  // ── trigger run ──
  const triggerRun = async () => {
    clearMessages();
    setTriggering(true);
    try {
      const data = await adminApi.startGapRun({
        syllabusId: selectedSyllabusId, jdIds: selectedJdIds,
      });
      setInfo(`Run started: ${data.run.id}`);
      setSelectedRunId(data.run.id);
      setTab('runs');
      await loadRuns();
    } catch (err) {
      setError(`Trigger run: ${err.response?.data?.error || err.message}`);
    } finally {
      setTriggering(false);
    }
  };

  // ── runs tab ──
  const loadRuns = useCallback(async () => {
    try {
      const data = await adminApi.listGapRuns(selectedSyllabusId || null);
      setRuns(data.runs || []);
    } catch (err) {
      setError(`Load runs: ${err.response?.data?.error || err.message}`);
    }
  }, [selectedSyllabusId]);

  useEffect(() => {
    if (tab === 'runs') loadRuns();
  }, [tab, loadRuns]);

  const loadRunDetail = useCallback(async (id) => {
    if (!id) { setRunDetail(null); return; }
    try {
      const data = await adminApi.getGapRun(id);
      setRunDetail(data.run);
    } catch (err) {
      setError(`Load run: ${err.response?.data?.error || err.message}`);
    }
  }, []);

  useEffect(() => {
    loadRunDetail(selectedRunId);
    // Auto-poll while running (and not stale).
    if (!selectedRunId) return;
    const iv = setInterval(() => loadRunDetail(selectedRunId), 5000);
    return () => clearInterval(iv);
  }, [selectedRunId, loadRunDetail]);

  // ── gap map tab ──
  const loadGapMapAndCandidates = useCallback(async (id) => {
    if (!id) { setGapMap([]); setCandidates([]); return; }
    try {
      const [mapData, candData] = await Promise.all([
        adminApi.getGapMap(id),
        adminApi.listGapRunCandidates(id, 'unresolved'),
      ]);
      setGapMap(mapData.gapMap || []);
      setCandidates(candData.candidates || []);
    } catch (err) {
      setError(`Load gap map: ${err.response?.data?.error || err.message}`);
    }
  }, []);

  useEffect(() => {
    if (tab === 'gapmap') loadGapMapAndCandidates(selectedRunId);
  }, [tab, selectedRunId, loadGapMapAndCandidates]);

  const verdictCounts = useMemo(() => {
    const c = { covered: 0, gap: 0, surplus: 0, absent: 0 };
    for (const row of gapMap) if (c[row.verdict] != null) c[row.verdict]++;
    return c;
  }, [gapMap]);

  // ── render ──
  return (
    <AdminLayout showBackButton backPath="/admin/dashboard">
      <div style={{ padding: 24, maxWidth: 1400, margin: '0 auto' }}>
        <h1 style={{ marginBottom: 4 }}>Curriculum Gap Engine</h1>
        <p style={{ color: '#6b7280', marginTop: 0 }}>
          S0 — pre-engagement analysis. Upload a syllabus, paste live job descriptions,
          get a per-capability verdict against a domain's canonical capability set.
        </p>

        {error && (
          <div style={{ padding: 12, background: '#fee2e2', border: '1px solid #dc2626',
                         borderRadius: 6, marginBottom: 12, color: '#991b1b' }}>
            {error}
          </div>
        )}
        {info && (
          <div style={{ padding: 12, background: '#dcfce7', border: '1px solid #059669',
                         borderRadius: 6, marginBottom: 12, color: '#065f46' }}>
            {info}
          </div>
        )}

        <div style={{ borderBottom: '1px solid #e5e7eb', marginBottom: 16 }}>
          {TABS.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              style={{
                padding: '10px 18px', border: 'none', background: 'transparent',
                borderBottom: tab === t.id ? '2px solid #00BCD4' : '2px solid transparent',
                fontWeight: tab === t.id ? 600 : 400, cursor: 'pointer',
              }}
            >{t.label}</button>
          ))}
        </div>

        {tab === 'syllabi' && (
          <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: 16 }}>
            {/* left: list + upload */}
            <div>
              <h3>Existing Syllabi ({syllabi.length})</h3>
              <div style={{ maxHeight: 300, overflow: 'auto', border: '1px solid #e5e7eb',
                             borderRadius: 6, padding: 4, marginBottom: 16 }}>
                {syllabi.map(s => (
                  <div key={s.id}
                    onClick={() => setSelectedSyllabusId(s.id)}
                    style={{
                      padding: 8, cursor: 'pointer', borderRadius: 4,
                      background: s.id === selectedSyllabusId ? '#e0f7fa' : 'transparent',
                    }}>
                    <div style={{ fontWeight: 600 }}>{s.programme_name}</div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>
                      {s.institution_name} · {s.domain_slug || s.domain_id} · {s.unit_count} units · {s.jd_count} JDs
                    </div>
                  </div>
                ))}
                {syllabi.length === 0 && <div style={{ padding: 8, color: '#6b7280' }}>No syllabi yet.</div>}
              </div>

              <h3>Upload New</h3>
              <form onSubmit={onUpload} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <input type="text" placeholder="Institution name"
                  value={uploadForm.institutionName}
                  onChange={e => setUploadForm(f => ({ ...f, institutionName: e.target.value }))} />
                <input type="text" placeholder="Programme (e.g. MCA, B.Tech CSE)"
                  value={uploadForm.programmeName}
                  onChange={e => setUploadForm(f => ({ ...f, programmeName: e.target.value }))} />
                <select value={uploadForm.domainId}
                  onChange={e => setUploadForm(f => ({ ...f, domainId: e.target.value }))}>
                  <option value="">-- Select domain --</option>
                  {domains.map(d => <option key={d.id} value={d.id}>{d.slug}</option>)}
                </select>
                <input type="file" accept=".pdf,.docx,.doc,.txt,text/plain"
                  onChange={e => setUploadForm(f => ({ ...f, file: e.target.files[0] || null }))} />
                <details>
                  <summary style={{ cursor: 'pointer', color: '#6b7280' }}>Or paste text</summary>
                  <textarea rows={5} placeholder="Paste extracted syllabus text..."
                    value={uploadForm.extractedText}
                    onChange={e => setUploadForm(f => ({ ...f, extractedText: e.target.value }))}
                    style={{ width: '100%', marginTop: 4 }} />
                </details>
                <button type="submit" disabled={uploading}
                  style={{ padding: 10, background: '#00BCD4', color: 'white', border: 'none',
                           borderRadius: 4, cursor: 'pointer', fontWeight: 600 }}>
                  {uploading ? 'Uploading…' : 'Upload & Split'}
                </button>
              </form>
            </div>

            {/* right: detail */}
            <div>
              {!syllabusDetail && (
                <div style={{ padding: 24, color: '#6b7280' }}>
                  Select a syllabus on the left, or upload a new one.
                </div>
              )}
              {syllabusDetail && (
                <div>
                  <h3>{syllabusDetail.programme_name} — {syllabusDetail.institution_name}</h3>

                  {(syllabusDetail.units || []).some(u => u.splitStrategy === 'chunks') && (
                    <div style={{ padding: 10, background: '#fef3c7', border: '1px solid #f59e0b',
                                   borderRadius: 4, marginBottom: 12, color: '#92400e' }}>
                      No unit/module/chapter headings detected — using fixed-size chunks.
                      Review carefully and correct boundaries below.
                    </div>
                  )}

                  <div style={{ marginBottom: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <strong>Units ({(syllabusDetail.units || []).length})</strong>
                      <div>
                        <button onClick={addUnit} style={{ marginRight: 6 }}>+ Add unit</button>
                        <button onClick={saveUnits} style={{
                          padding: '6px 12px', background: '#059669', color: 'white',
                          border: 'none', borderRadius: 4, cursor: 'pointer' }}>
                          Save units
                        </button>
                      </div>
                    </div>
                    <div style={{ maxHeight: 400, overflow: 'auto', border: '1px solid #e5e7eb',
                                   borderRadius: 6, padding: 8, marginTop: 8 }}>
                      {(syllabusDetail.units || []).map((u, i) => (
                        <div key={i} style={{ marginBottom: 12, padding: 8,
                                              border: '1px solid #f3f4f6', borderRadius: 4 }}>
                          <div style={{ display: 'flex', gap: 8, marginBottom: 4 }}>
                            <span style={{ padding: '4px 8px', background: '#f3f4f6', borderRadius: 4 }}>
                              #{u.ordinal}
                            </span>
                            <input type="text" value={u.heading || ''}
                              onChange={e => updateUnitHeading(i, e.target.value)}
                              style={{ flex: 1 }} />
                            <button onClick={() => removeUnit(i)}
                              style={{ color: '#dc2626' }}>Remove</button>
                          </div>
                          <textarea value={u.text || ''} rows={4}
                            onChange={e => updateUnitText(i, e.target.value)}
                            style={{ width: '100%' }} />
                        </div>
                      ))}
                    </div>
                  </div>

                  <div style={{ marginBottom: 16 }}>
                    <strong>Job Descriptions ({jds.length})</strong>
                    <div style={{ marginTop: 8 }}>
                      {jds.map(j => (
                        <label key={j.id} style={{ display: 'flex', gap: 8, padding: 6,
                                                    borderBottom: '1px solid #f3f4f6' }}>
                          <input type="checkbox"
                            checked={selectedJdIds.includes(j.id)}
                            onChange={e => setSelectedJdIds(prev =>
                              e.target.checked ? [...prev, j.id] : prev.filter(x => x !== j.id))} />
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 600 }}>{j.label || '(no label)'}</div>
                            <div style={{ fontSize: 12, color: '#6b7280' }}>
                              {j.preview}{j.preview && j.preview.length >= 400 ? '…' : ''}
                            </div>
                          </div>
                        </label>
                      ))}
                    </div>
                    <div style={{ marginTop: 12, padding: 8, background: '#f9fafb', borderRadius: 4 }}>
                      <input type="text" placeholder="Label (optional, e.g. 'SDE1 - Infosys')"
                        value={newJdLabel}
                        onChange={e => setNewJdLabel(e.target.value)}
                        style={{ width: '100%', marginBottom: 6 }} />
                      <textarea rows={4} placeholder="Paste job description text..."
                        value={newJdText}
                        onChange={e => setNewJdText(e.target.value)}
                        style={{ width: '100%' }} />
                      <button onClick={addJd} style={{
                        marginTop: 6, padding: '6px 12px', background: '#00BCD4', color: 'white',
                        border: 'none', borderRadius: 4, cursor: 'pointer' }}>
                        + Add JD
                      </button>
                    </div>
                  </div>

                  <button onClick={triggerRun} disabled={triggering}
                    style={{
                      padding: 12, background: triggering ? '#9ca3af' : '#dc2626',
                      color: 'white', border: 'none', borderRadius: 4,
                      fontWeight: 600, cursor: triggering ? 'not-allowed' : 'pointer',
                      width: '100%',
                    }}>
                    {triggering ? 'Triggering…'
                      : `Trigger Gap Run  (${selectedJdIds.length} JDs selected)`}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {tab === 'runs' && (
          <div>
            <div style={{ marginBottom: 12 }}>
              {selectedSyllabusId && (
                <button onClick={() => setSelectedSyllabusId(null)}
                  style={{ marginRight: 8 }}>Show all runs</button>
              )}
              <button onClick={loadRuns}>Refresh</button>
              {selectedSyllabusId && <span style={{ marginLeft: 8, color: '#6b7280' }}>
                Filtered by syllabus.</span>}
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f9fafb', textAlign: 'left' }}>
                  <th style={{ padding: 8 }}>Started</th>
                  <th style={{ padding: 8 }}>Status</th>
                  <th style={{ padding: 8 }}>Syllabus</th>
                  <th style={{ padding: 8 }}>Discarded</th>
                  <th style={{ padding: 8 }}>Error</th>
                  <th style={{ padding: 8 }}></th>
                </tr>
              </thead>
              <tbody>
                {runs.map(r => (
                  <tr key={r.id} style={{ borderBottom: '1px solid #f3f4f6',
                                            background: r.id === selectedRunId ? '#e0f7fa' : 'transparent' }}>
                    <td style={{ padding: 8, fontSize: 13 }}>{new Date(r.started_at).toLocaleString()}</td>
                    <td style={{ padding: 8 }}><StatusBadge status={r.status} stale={r.stale} /></td>
                    <td style={{ padding: 8, fontSize: 12, color: '#6b7280' }}>{r.syllabus_id.slice(0, 8)}…</td>
                    <td style={{ padding: 8 }}>{r.discarded_count}</td>
                    <td style={{ padding: 8, fontSize: 12, color: '#dc2626', maxWidth: 300, overflow: 'hidden' }}>
                      {r.error ? r.error.slice(0, 80) : ''}
                    </td>
                    <td style={{ padding: 8 }}>
                      <button onClick={() => { setSelectedRunId(r.id); setTab('gapmap'); }}>
                        View
                      </button>
                    </td>
                  </tr>
                ))}
                {runs.length === 0 && (
                  <tr><td colSpan={6} style={{ padding: 16, color: '#6b7280' }}>No runs yet.</td></tr>
                )}
              </tbody>
            </table>
            {runDetail && (
              <div style={{ marginTop: 24, padding: 12, border: '1px solid #e5e7eb', borderRadius: 6 }}>
                <h3>Run detail — {selectedRunId?.slice(0, 8)}…</h3>
                <p>Status: <StatusBadge status={runDetail.status} stale={runDetail.stale} /></p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 8 }}>
                  <div>Candidates: <strong>{runDetail.candidate_count}</strong></div>
                  <div>Resolved: <strong>{runDetail.resolved_count}</strong></div>
                  <div>Unresolved: <strong>{runDetail.unresolved_count}</strong></div>
                  <div>Gap-map rows: <strong>{runDetail.gap_map_row_count}</strong></div>
                </div>
                {runDetail.error && (
                  <div style={{ padding: 8, background: '#fee2e2', color: '#991b1b', borderRadius: 4,
                                 whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: 12 }}>
                    {runDetail.error}
                  </div>
                )}
                {runDetail.discarded_count > 0 && (
                  <details style={{ marginTop: 8 }}>
                    <summary>Discarded proposals ({runDetail.discarded_count})</summary>
                    <pre style={{ fontSize: 11, maxHeight: 300, overflow: 'auto' }}>
                      {JSON.stringify(runDetail.discards, null, 2)}
                    </pre>
                  </details>
                )}
              </div>
            )}
          </div>
        )}

        {tab === 'gapmap' && (
          <div>
            {!selectedRunId && <div style={{ color: '#6b7280' }}>Select a run in the Runs tab first.</div>}
            {selectedRunId && (
              <>
                <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
                  <div>Covered: <strong style={{ color: '#059669' }}>{verdictCounts.covered}</strong></div>
                  <div>Gap: <strong style={{ color: '#dc2626' }}>{verdictCounts.gap}</strong></div>
                  <div>Surplus: <strong style={{ color: '#d97706' }}>{verdictCounts.surplus}</strong></div>
                  <div>Absent: <strong style={{ color: '#6b7280' }}>{verdictCounts.absent}</strong></div>
                </div>

                <h3>Gap map ({gapMap.length} capabilities)</h3>
                <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 24 }}>
                  <thead>
                    <tr style={{ background: '#f9fafb', textAlign: 'left' }}>
                      <th style={{ padding: 8 }}>Code</th>
                      <th style={{ padding: 8 }}>Statement</th>
                      <th style={{ padding: 8 }}>Verdict</th>
                      <th style={{ padding: 8 }}>Taught</th>
                      <th style={{ padding: 8 }}>Demanded</th>
                    </tr>
                  </thead>
                  <tbody>
                    {gapMap.map(row => (
                      <tr key={row.capability_id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <td style={{ padding: 8, fontFamily: 'monospace' }}>{row.code}</td>
                        <td style={{ padding: 8, fontSize: 13 }}>{row.statement}</td>
                        <td style={{ padding: 8 }}><VerdictBadge verdict={row.verdict} /></td>
                        <td style={{ padding: 8 }}>{row.taught_count}</td>
                        <td style={{ padding: 8 }}>{row.demanded_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <h3>Unresolved candidates ({candidates.length})</h3>
                <p style={{ color: '#6b7280', fontSize: 13 }}>
                  Proposals the deterministic resolver could not map to a canonical capability code.
                  Review and resolve manually if the analyst's intent is clear.
                </p>
                {candidates.length === 0 && <div style={{ color: '#6b7280' }}>None.</div>}
                {candidates.map(c => (
                  <div key={c.id} style={{ marginBottom: 8, padding: 8,
                                            border: '1px solid #e5e7eb', borderRadius: 4 }}>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>
                      side={c.side} · code={c.proposed_capability_code || '(none)'} ·
                      demand_source={c.demand_source || 'n/a'}
                      {c.rejection_reason && (
                        <span style={{
                          marginLeft: 8, padding: '1px 6px', borderRadius: 3,
                          background: c.rejection_reason === 'no_code_proposed' ? '#e0e7ff' : '#fee2e2',
                          color: c.rejection_reason === 'no_code_proposed' ? '#3730a3' : '#991b1b',
                          fontWeight: 600,
                        }}>{c.rejection_reason}</span>
                      )}
                      {c.source_span && c.source_span.ambiguous && (
                        <span style={{
                          marginLeft: 8, padding: '1px 6px', borderRadius: 3,
                          background: '#fef3c7', color: '#92400e', fontWeight: 600,
                        }}>ambiguous quote</span>
                      )}
                    </div>
                    <div style={{ marginTop: 4 }}>{c.raw_text}</div>
                  </div>
                ))}
              </>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
